import { request as http } from '@/utils/http'

// ==================== 船期聚合查询相关类型 ====================

// 船期聚合查询请求参数
export interface ShippingScheduleAggregationRequest {
  pol: string // 起运港五字码(CargowareX港口五字码)
  pod: string // 目的港五字码(CargowareX港口五字码)
  etd: string // ETD时间(格式:yyyy-MM-dd)
  scheduleIds?: string[] // 船期ID列表 - 用于明细查询
  page?: number // 页码(从1开始) - 用于分页查询
  pageSize?: number // 每页数量 - 用于分页查询
  carrierCdList?: string[] // 船公司列表(云当code)
  polTerminalCdList?: string[] // 起运港码头列表(云当code)
  podTerminalCdList?: string[] // 目的港码头列表(云当code)
}

// 直航/中转统计信息
export interface AggregationDirectTransitVO {
  vesselTotal: number // 船数量
  carrierTotal: number // 船公司数量
  scheduleTotal: number // 船期数量
  transferModeNumber: number // 方案数，转运方式数量
}

// 船期聚合列表项
export interface AggregationListVO {
  scheduleIds: string[] // 该分组涉及的全部船期ID
  carrierCdList: string[] // 船公司列表(云当code)
  polTerminalCdList: string[] // 起运港码头列表(云当code)
  podTerminalCdList: string[] // 目的港码头列表(云当code)
  routeEtd: string // 计划离港班期(周几)
  mainCarrierCode: string // 共舱时共舱主船公司代码
  mainSvcCode: string // 共舱时共舱主船航线代码
  shareCabins: string // 共舱信息(JSON字符串)
  totalDuration: string // 航程区间(如:32～40天)
  carrierCd: string // 船公司
  transferModeNumber: number // 方案数(转运方式数量)
}

// 船期聚合查询响应
export interface ShippingScheduleAggregationVO {
  directInfo: AggregationDirectTransitVO // 直航统计信息
  transitInfo: AggregationDirectTransitVO // 中转统计信息
  directItem: AggregationListVO[] // 船期直达数据列表
  transitItem: AggregationListVO[] // 船期中转数据列表
}

// 船期共舱信息VO
export interface ShippingScheduleShareCabinVO {
  carrierCode: string // 共享承运人代码
  carrierBaseId: number | null // 船公司基础数据ID
  carrierBaseCode: string // 船公司基础数据code
  carrierBaseCn: string // 船公司基础数据中文名
  carrierBaseEn: string // 船公司基础数据英文名
  svcCode: string // 共享航线代码
  voyage: string // 航次
  isMainCarrier: boolean // 是否主船公司
}

// 共舱承运人操作时间VO
export interface ShareCabinOpTimeVO {
  carrierCode: string // 承运人代码
  carrierBaseId: number | null // 船公司基础数据ID
  carrierBaseCode: string // 船公司基础数据code
  carrierBaseCn: string // 船公司基础数据中文名
  carrierBaseEn: string // 船公司基础数据英文名
  isMainCarrier: boolean // 是否主承运人
  bookingCutoff: string // 截订舱时间
  cyOpen: string // 进港时间
  cyClose: string // 截港时间
  customCutoff: string // 截放行
  cutOff: string // 截申报
  siCutoff: string // 截提单时间
  vgmCutoff: string // 截VGM时间
}

// 船期中转航段信息VO
export interface ShippingScheduleRouteVO {
  routeNo: number // 航段序号(从1开始)
  vessel: string // 船名
  voyage: string // 航次
  svcCode: string // 航线代码
  pol: string // 起运港名称
  polCode: string // 起运港代码
  polBaseId: number | null // 起运港基础数据ID
  polBaseCode: string // 起运港基础数据code
  polBaseCn: string // 起运港基础数据中文名
  polBaseEn: string // 起运港基础数据英文名
  polTerminal: string // 起运港码头
  polTerminalCode: string // 起运港码头代码
  pod: string // 目的港名称
  podCode: string // 目的港代码
  podBaseId: number | null // 目的港基础数据ID
  podBaseCode: string // 目的港基础数据code
  podBaseCn: string // 目的港基础数据中文名
  podBaseEn: string // 目的港基础数据英文名
  podTerminal: string // 目的港码头
  podTerminalCode: string // 目的港码头代码
  etd: string // 预计离港时间
  eta: string // 预计到港时间
  transitTime: string // 本段运输时间(天)
  carriageType: string // 运输类型：OCEAN-海运/RAIL-铁路/TRUCK-卡车
}

// 船期聚合明细项
export interface ShippingScheduleAggregationItemVO {
  id: string // 唯一标识
  dateCreated: string // 创建时间
  tenantId: string // 租户ID
  routeCd: string // 航线服务名称
  routeEtd: string // 计划离港班期(周几)
  imo: string // IMO编号
  mmsi: string // MMSI编号
  callSign: string // 呼号
  carrierCd: string // 船公司
  carrierBaseId: string // 船公司基础数据ID
  carrierBaseCode: string // 船公司基础数据code
  carrierBaseCn: string // 船公司基础数据中文名
  carrierBaseEn: string // 船公司基础数据英文名
  isReferenceCarrier: boolean // 是否主船东(0=否, 1=是)
  vessel: string // 船名
  voyage: string // 航次
  shipAgency: string // 船代
  polCd: string // 起运港五字码
  pol: string // 起运港
  polBaseId: string // 起运港基础数据ID
  polBaseCode: string // 起运港基础数据code
  polBaseCn: string // 起运港基础数据中文名
  polBaseEn: string // 起运港基础数据英文名
  polTerminal: string // 起运港码头
  polTerminalCd: string // 起运港码头代码
  podCd: string // 目的港五字码
  pod: string // 目的港
  podBaseId: string // 目的港基础数据ID
  podBaseCode: string // 目的港基础数据code
  podBaseCn: string // 目的港基础数据中文名
  podBaseEn: string // 目的港基础数据英文名
  podTerminal: string // 目的港码头
  podTerminalCd: string // 目的港码头代码
  eta: string // 预计抵达时间
  etd: string // 预计离开时间
  totalDuration: string // 预计航程
  isTransit: boolean // 是否转运
  transitPortEn: string // 中转1港口名
  transitPortCd: string // 中转1港口代码
  transitPortBaseId: string // 中转1港口基础数据ID
  transitPortBaseCode: string // 中转1港口基础数据code
  transitPortBaseCn: string // 中转1港口基础数据中文名
  transitPortBaseEn: string // 中转1港口基础数据英文名
  transitSvcCode: string // 中转1航线代码
  vesselAfterTransit: string // 中转1船名
  voyageAfterTransit: string // 中转1航次
  secondTransitPortEn: string // 中转2港口名
  secondTransitPortCd: string // 中转2港口代码
  secondTransitPortBaseId: string // 中转2港口基础数据ID
  secondTransitPortBaseCode: string // 中转2港口基础数据code
  secondTransitPortBaseCn: string // 中转2港口基础数据中文名
  secondTransitPortBaseEn: string // 中转2港口基础数据英文名
  secondTransitSvcCode: string // 中转2航线代码
  secondVesselAfterTransit: string // 中转2船名
  secondVoyageAfterTransit: string // 中转2航次
  bookingCutoff: string // 截订舱时间
  cyOpen: string // 进港时间
  cyClose: string // 截港时间
  customCutoff: string // 截放行时间
  cutOff: string // 截申报时间
  siCutoff: string // 截提单时间
  vgmCutoff: string // 截VGM时间
  shareCabinList: ShippingScheduleShareCabinVO[] // 共舱信息列表
  shareScheduleId: string // 共舱组船期ID
  transitRoutes?: ShippingScheduleRouteVO[] // 中转航段信息列表
  shareCabinOpTimes?: ShareCabinOpTimeVO[] // 各承运人操作时间列表（直航）
}

// 分页响应
export interface Page<T> {
  page: number // 当前页码
  pageSize: number // 每页数量
  total: number // 总记录数
  records: T[] // 数据列表
}

// 船期聚合明细项
export interface ShippingSchedulePortalPortVO {
  type: number; // 类型：1-起运港；2-目的港
  regionCn: string; // 国内行政区
  overseasArea: string; // 海外大区
  overseasSubarea: string; // 海外子区
  code: string; // 港口代码
  nameCn: string; // 港口中文
  nameEn: string; // 港口英文
}


// ==================== 船期聚合查询API ====================

// 查询船期数据聚合版本
export const getAggregationList = (params: ShippingScheduleAggregationRequest) => {
  return http.post<ShippingScheduleAggregationVO>('/mdm/shippingSchedule/aggregation/list', params)
}

// 分页查询船期聚合明细
export const getAggregationItemPage = (params: ShippingScheduleAggregationRequest) => {
  return http.post<Page<ShippingScheduleAggregationItemVO>>('/mdm/shippingSchedule/aggregation/item', params)
}


// 查询船期门户港口信息
export const getPortalPortList = () => {
  return http.post<ShippingSchedulePortalPortVO[]>('/mdm/shippingSchedule/portalPort/list')
}

// 整箱运价匹配船期请求
export interface FclPriceScheduleMatchRequest {
  /** ID 用 string，避免雪花精度丢失 */
  carrierId?: string | number | null
  pol?: string | number | null
  pod?: string | number | null
  pot1?: string | number | null
  pot2?: string | number | null
  pot3?: string | number | null
  laneCode?: string
  directTransit?: number
  vessel?: string
  voyage?: string
  beginDate?: string
  endDate?: string
}

// 整箱运价匹配船期
export const matchScheduleByFclPrice = (params: FclPriceScheduleMatchRequest) => {
  return http.post<ShippingScheduleAggregationItemVO[]>('/mdm/shippingSchedule/fclPrice/match', params)
}
